import { useEffect, useState } from "react";
import { X, Shield, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../api/client";

/**
 * Admin-only dialog that exposes a team member's saved security questions
 * AND answers (plaintext) so support staff can verify identity over the
 * phone if the user can't self-serve a reset.
 */
export default function SecurityQADialog({ open, worker, onClose }) {
  const [bank, setBank] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [edits, setEdits] = useState([{ question_id: "", answer: "" }, { question_id: "", answer: "" }]);

  useEffect(() => {
    if (!open || !worker?.id) return;
    setLoading(true);
    Promise.all([
      api.get(`/workers/${worker.id}/security-questions`),
      api.get("/auth/security-questions/options"),
    ])
      .then(([qa, bk]) => {
        setData(qa.data);
        setBank(bk.data);
        const saved = qa.data.questions || [];
        if (saved.length === 2) {
          setEdits([
            { question_id: saved[0].question_id, answer: saved[0].answer },
            { question_id: saved[1].question_id, answer: saved[1].answer },
          ]);
        } else if (bk.data.length >= 2) {
          setEdits([
            { question_id: bk.data[0].id, answer: "" },
            { question_id: bk.data[1].id, answer: "" },
          ]);
        }
      })
      .catch(() => toast.error("Could not load security questions"))
      .finally(() => setLoading(false));
  }, [open, worker?.id]);

  const close = () => { setEditing(false); setData(null); onClose(); };

  const save = async () => {
    if (edits[0].question_id === edits[1].question_id) {
      toast.error("Questions must be distinct");
      return;
    }
    if (!edits[0].answer.trim() || !edits[1].answer.trim()) {
      toast.error("Answers cannot be empty");
      return;
    }
    setLoading(true);
    try {
      await api.put(`/workers/${worker.id}/security-questions`, {
        security_questions: edits.map((e) => ({
          question_id: e.question_id,
          answer: e.answer.trim(),
        })),
      });
      toast.success("Security questions updated");
      setEditing(false);
      // Reload data
      const { data } = await api.get(`/workers/${worker.id}/security-questions`);
      setData(data);
    } catch {
      toast.error("Failed to save");
    } finally {
      setLoading(false);
    }
  };

  if (!open || !worker) return null;
  const saved = data?.questions || [];

  return (
    <div className="fixed inset-0 z-[55] bg-black/50 grid place-items-center p-4 overflow-y-auto" onClick={close}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white border border-gray-300 max-w-lg w-full p-6 rounded-sm shadow-2xl my-6"
        data-testid="security-qa-dialog"
      >
        <div className="flex items-start gap-3 mb-5">
          <div className="bg-[#002FA7] text-white p-2 rounded-sm"><Shield className="w-5 h-5" /></div>
          <div className="flex-1">
            <h4 className="font-black text-xl tracking-tight">Security Q&amp;A</h4>
            <p className="text-xs text-gray-500 mt-1">
              {worker.name} · <span className="font-mono">{worker.email}</span>
            </p>
          </div>
          <button onClick={close}><X className="w-5 h-5" /></button>
        </div>

        {loading && (
          <div className="py-6 text-center text-sm text-gray-500 flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        )}

        {!loading && !editing && saved.length === 0 && (
          <div className="text-sm text-gray-600 mb-5" data-testid="sqa-empty">
            No security questions on file. The team member cannot use the
            self-serve &ldquo;Forgot password&rdquo; flow until you set them.
          </div>
        )}

        {!loading && !editing && saved.length > 0 && (
          <div className="space-y-3 mb-5">
            {saved.map((q, i) => (
              <div key={q.question_id} className="border-l-4 border-[#002FA7] bg-[#F0F4FF] px-3 py-2" data-testid={`sqa-row-${i}`}>
                <div className="text-[10px] uppercase tracking-widest text-gray-500">{q.text}</div>
                <div className="font-mono text-sm text-gray-900 mt-0.5">{q.answer}</div>
              </div>
            ))}
          </div>
        )}

        {!loading && editing && (
          <div className="space-y-3 mb-5">
            {[0, 1].map((i) => (
              <div key={i}>
                <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">
                  Question {i + 1}
                </label>
                <select
                  value={edits[i].question_id}
                  onChange={(e) =>
                    setEdits((p) => p.map((row, idx) => idx === i ? { ...row, question_id: e.target.value } : row))
                  }
                  data-testid={`sqa-edit-q${i}`}
                  className="w-full border border-gray-300 px-3 py-2 mb-1 rounded-sm text-sm bg-white"
                >
                  {bank.map((q) => (
                    <option
                      key={q.id}
                      value={q.id}
                      disabled={q.id === edits[1 - i].question_id}
                    >
                      {q.text}
                    </option>
                  ))}
                </select>
                <input
                  required
                  placeholder="Answer"
                  autoComplete="off"
                  value={edits[i].answer}
                  onChange={(e) =>
                    setEdits((p) => p.map((row, idx) => idx === i ? { ...row, answer: e.target.value } : row))
                  }
                  data-testid={`sqa-edit-a${i}`}
                  className="w-full border border-gray-300 px-3 py-2 rounded-sm text-sm"
                />
              </div>
            ))}
          </div>
        )}

        {!loading && (
          <div className="flex gap-2">
            {!editing && (
              <button
                onClick={() => setEditing(true)}
                data-testid="sqa-edit-button"
                className="flex-1 border border-gray-300 px-3 py-2 rounded-sm text-sm font-semibold hover:bg-gray-100"
              >
                {saved.length ? "Edit" : "Set up"} questions
              </button>
            )}
            {editing && (
              <>
                <button
                  onClick={() => setEditing(false)}
                  className="flex-1 border border-gray-300 px-3 py-2 rounded-sm text-sm hover:bg-gray-100"
                  data-testid="sqa-cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={save}
                  data-testid="sqa-save"
                  className="flex-1 bg-[#002FA7] text-white px-3 py-2 rounded-sm text-sm font-bold hover:bg-[#002277]"
                >
                  Save
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
